import { ethers } from 'ethers';
import { SWAP_ABI } from '../constants';
import { parseError } from '../utils/errorParser';
import {
  createSubmittedFailureResult,
  createSubmittedUnknownResult,
  createSwapSuccessResult,
} from './submissionResult';

const READ_TIMEOUT_MS = 15000;
const WALLET_REQUEST_TIMEOUT_MS = 60000;
const SIMULATION_TIMEOUT_MS = 30000;
const CONFIRMATION_TIMEOUT_MS = 180000;

const withTimeout = (promise, timeoutMs, message) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const unique = values => Array.from(new Set(values.filter(Boolean)));

const getRpcOrigin = rpcUrl => {
  try {
    return new URL(rpcUrl).origin;
  } catch {
    return 'configured rpc';
  }
};

const createJsonRpcProvider = (rpcUrl, networkId) => {
  const numericNetworkId = Number(networkId);
  if (Number.isFinite(numericNetworkId) && numericNetworkId > 0) {
    return new ethers.JsonRpcProvider(rpcUrl, numericNetworkId, { staticNetwork: true });
  }
  return new ethers.JsonRpcProvider(rpcUrl);
};

export const createEvmAdapter = config => {
  const getReadProviders = provider => {
    const rpcProviders = unique([config?.rpcUrl, ...(config?.rpcFallbackUrls || [])]).map(
      (rpcUrl, index) => ({
        label: index === 0 ? 'configured rpc' : `fallback rpc ${index}`,
        origin: getRpcOrigin(rpcUrl),
        provider: createJsonRpcProvider(rpcUrl, config?.networkId),
      })
    );
    const walletProviders = provider
      ? [
          {
            label: 'wallet provider',
            origin: 'wallet',
            provider: new ethers.BrowserProvider(provider),
          },
        ]
      : [];

    if (rpcProviders.length) return rpcProviders;
    return walletProviders;
  };

  const getPrimaryConfiguredReadProvider = () => {
    if (!config?.rpcUrl) {
      throw new Error(`${config?.networkLabel || 'Selected network'} RPC is not configured.`);
    }

    return {
      label: 'configured chain rpc',
      origin: getRpcOrigin(config.rpcUrl),
      provider: createJsonRpcProvider(config.rpcUrl, config?.networkId),
    };
  };

  const readWithFallback = async ({ provider, operation, timeoutMessage }) => {
    const providers = getReadProviders(provider);
    if (!providers.length) {
      throw new Error('EVM provider is still loading. Reconnect your wallet and try again.');
    }

    let lastError;
    for (const readSource of providers) {
      try {
        return await withTimeout(
          operation(readSource.provider, readSource),
          READ_TIMEOUT_MS,
          timeoutMessage
        );
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  };

  const ensureConfigured = inputAsset => {
    if (!config?.usdnAddress || !config?.swapContractAddress || !config?.approveContractAddress) {
      throw new Error(
        `${config?.networkLabel || 'Selected network'} swap contracts are not configured.`
      );
    }
    if (!inputAsset?.address) {
      throw new Error(
        `${config?.networkLabel || 'Selected network'} input token is not configured.`
      );
    }
  };

  const getCapabilities = () => ({
    requiresApproval: true,
    supportsSimulation: true,
    supportsNetworkSwitch: true,
  });

  const getBalances = async ({ account, inputAsset }) => {
    ensureConfigured(inputAsset);
    const erc20ReadAbi = ['function balanceOf(address account) view returns (uint256)'];
    const diagnostics = {
      account,
      inputAddress: inputAsset.address,
      outputAddress: config.usdnAddress,
      inputAttempts: [],
      outputAttempts: [],
    };

    const readTokenBalance = async ({ tokenAddress, readSource, timeoutMessage }) => {
      const raw = await withTimeout(
        new ethers.Contract(tokenAddress, erc20ReadAbi, readSource.provider).balanceOf(account),
        READ_TIMEOUT_MS,
        timeoutMessage
      );
      return raw;
    };

    const readInputBalance = async () => {
      const readSource = getPrimaryConfiguredReadProvider();

      try {
        const result = await readTokenBalance({
          tokenAddress: inputAsset.address,
          readSource,
          timeoutMessage:
            'Input token balance request timed out. Please check the connected wallet provider.',
        });
        diagnostics.inputAttempts.push({
          source: `${readSource.label} (${readSource.origin})`,
          result: ethers.formatUnits(result, inputAsset.decimals),
        });
        return result;
      } catch (error) {
        diagnostics.inputAttempts.push({
          source: `${readSource.label} (${readSource.origin})`,
          error: error?.message || String(error),
        });
        throw error;
      }
    };

    const inputRaw = await readInputBalance();

    let outputRaw = 0n;
    try {
      const readSource = getPrimaryConfiguredReadProvider();
      outputRaw = await readTokenBalance({
        tokenAddress: config.usdnAddress,
        readSource,
        timeoutMessage:
          'USDN balance request timed out. Please check the connected wallet provider.',
      });
      diagnostics.outputAttempts.push({
        source: `${readSource.label} (${readSource.origin})`,
        result: ethers.formatUnits(outputRaw, 6),
      });
    } catch (error) {
      diagnostics.outputAttempts.push({
        source: `configured chain rpc (${getRpcOrigin(config?.rpcUrl)})`,
        error: error?.message || String(error),
      });
      outputRaw = 0n;
    }

    return {
      inputBalance: ethers.formatUnits(inputRaw, inputAsset.decimals),
      outputBalance: ethers.formatUnits(outputRaw, 6),
      diagnostics,
    };
  };

  const checkAllowanceLikeCapability = async ({ provider, account, inputAsset, amount }) => {
    if (!amount || Number(amount) <= 0) return { enough: false };
    ensureConfigured(inputAsset);
    const readAbi = ['function allowance(address owner, address spender) view returns (uint256)'];
    const allowance = await readWithFallback({
      provider,
      timeoutMessage: 'Allowance request timed out. Please check the selected RPC connection.',
      operation: readProvider => {
        const token = new ethers.Contract(inputAsset.address, readAbi, readProvider);
        return token.allowance(account, config.approveContractAddress);
      },
    });
    const amountUnits = ethers.parseUnits(amount, inputAsset.decimals);
    return { enough: allowance >= amountUnits };
  };

  const approveIfRequired = async ({ provider, amount, inputAsset }) => {
    ensureConfigured(inputAsset);
    const browserProvider = new ethers.BrowserProvider(provider);
    const signer = await browserProvider.getSigner();
    const approveAbi = [
      'function approve(address spender, uint256 amount)',
      'function allowance(address owner, address spender) view returns (uint256)',
    ];
    const token = new ethers.Contract(inputAsset.address, approveAbi, signer);
    const amountUnits = ethers.parseUnits(amount, inputAsset.decimals);

    const currentAllowance = await token.allowance(
      await signer.getAddress(),
      config.approveContractAddress
    );
    if (currentAllowance > 0n) {
      const resetTx = await token.approve(config.approveContractAddress, 0);
      await withTimeout(
        resetTx.wait(),
        CONFIRMATION_TIMEOUT_MS,
        'Approval reset was submitted, but confirmation timed out. Check your wallet or explorer before retrying.'
      );
    }

    const approveTx = await withTimeout(
      token.approve(config.approveContractAddress, amountUnits),
      WALLET_REQUEST_TIMEOUT_MS,
      'Approval request did not appear in the wallet. Reopen your connected wallet and try again.'
    );
    const receipt = await withTimeout(
      approveTx.wait(),
      CONFIRMATION_TIMEOUT_MS,
      'Approval was submitted, but confirmation timed out. Check your wallet or explorer before retrying.'
    );
    if (receipt.status === 0) {
      return {
        ok: false,
        status: 'Approval transaction failed',
        statusType: 'error',
      };
    }

    return {
      ok: true,
      status: 'Approved successfully! You can now swap.',
      statusType: 'success',
      txHash: approveTx.hash,
    };
  };

  const simulateSwap = async ({ provider, inputAsset, amount }) => {
    ensureConfigured(inputAsset);
    const browserProvider = new ethers.BrowserProvider(provider);
    const signer = await browserProvider.getSigner();
    const swapContract = new ethers.Contract(config.swapContractAddress, SWAP_ABI, signer);
    const amountInWei = ethers.parseUnits(amount, inputAsset.decimals);
    await withTimeout(
      swapContract.swap.staticCall(inputAsset.address, amountInWei, 0),
      SIMULATION_TIMEOUT_MS,
      'Swap pre-flight check timed out. Please check the selected wallet and RPC connection.'
    );
    return { ok: true };
  };

  const getReceiptByHash = async (browserProvider, hash) => {
    if (!hash) return null;
    const providers = [browserProvider];
    if (config.rpcUrl) {
      providers.push(createJsonRpcProvider(config.rpcUrl, config.networkId));
    }

    for (const receiptProvider of providers) {
      try {
        const receipt = await withTimeout(
          receiptProvider.getTransactionReceipt(hash),
          READ_TIMEOUT_MS,
          'Receipt lookup timed out.'
        );
        if (receipt) return receipt;
      } catch {
        // Try the next receipt provider.
      }
    }
    return null;
  };

  const pollReceiptByHash = async (browserProvider, hash) => {
    const deadline = Date.now() + CONFIRMATION_TIMEOUT_MS;
    while (Date.now() < deadline) {
      const receipt = await getReceiptByHash(browserProvider, hash);
      if (receipt) return receipt;
      await sleep(3000);
    }
    return null;
  };

  const waitForSubmittedReceipt = async (tx, browserProvider, hash) => {
    const waitForWalletReceipt = tx.wait().catch(async error => {
      const receipt = await getReceiptByHash(browserProvider, hash);
      if (receipt) return receipt;
      throw error;
    });

    const waitForChainReceipt = pollReceiptByHash(browserProvider, hash).then(receipt => {
      if (receipt) return receipt;
      throw new Error(
        'Swap transaction was submitted, but confirmation timed out. Check your wallet or explorer before retrying.'
      );
    });

    return withTimeout(
      Promise.race([waitForWalletReceipt, waitForChainReceipt]),
      CONFIRMATION_TIMEOUT_MS,
      'Swap transaction was submitted, but confirmation timed out. Check your wallet or explorer before retrying.'
    );
  };

  const submitSwap = async ({ provider, inputAsset, amount, onSubmitted }) => {
    let submittedHash = '';
    try {
      ensureConfigured(inputAsset);
      const browserProvider = new ethers.BrowserProvider(provider);
      const signer = await browserProvider.getSigner();
      const swapContract = new ethers.Contract(config.swapContractAddress, SWAP_ABI, signer);
      const amountInWei = ethers.parseUnits(amount, inputAsset.decimals);
      const tx = await withTimeout(
        swapContract.swap(inputAsset.address, amountInWei, 0),
        WALLET_REQUEST_TIMEOUT_MS,
        'Swap request did not appear in the wallet. Reopen your connected wallet and try again.'
      );
      submittedHash = tx.hash || '';
      if (submittedHash && typeof onSubmitted === 'function') {
        onSubmitted(submittedHash);
      }
      let receipt;
      try {
        receipt = await waitForSubmittedReceipt(tx, browserProvider, submittedHash);
      } catch (error) {
        const fallbackReceipt = await getReceiptByHash(browserProvider, submittedHash);
        if (fallbackReceipt?.status === 1) {
          return createSwapSuccessResult(submittedHash);
        }
        if (fallbackReceipt?.status === 0) {
          return createSubmittedFailureResult(submittedHash, error);
        }
        if (submittedHash) {
          return createSubmittedUnknownResult({
            txHash: submittedHash,
            explorerName: 'the explorer',
            error,
          });
        }
        return {
          ok: false,
          status: parseError(error),
          statusType: 'error',
          txHash: submittedHash || error?.transactionHash || error?.receipt?.hash || '',
        };
      }
      if (receipt.status === 0) {
        return createSubmittedFailureResult(submittedHash || receipt.hash || '', receipt);
      }
      return createSwapSuccessResult(submittedHash);
    } catch (error) {
      return {
        ok: false,
        status: parseError(error),
        statusType: 'error',
        txHash: submittedHash || error?.transactionHash || error?.receipt?.hash || '',
      };
    }
  };

  return {
    getCapabilities,
    getBalances,
    checkAllowanceLikeCapability,
    approveIfRequired,
    simulateSwap,
    submitSwap,
  };
};
